import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useCurrentOrganization, useHasAccess } from '@/modules/core/hooks';
import useGetEmployeeSSNQuery from '@/modules/employee/Employee/PersonalInformation/EmployerInfo/queries/useGetEmployeeSSN.query';

import { useAutoSearchHSACombineAccounts } from '../queries/useSearchHSACombineAccounts.query';
import { CombineHSASectionType } from '../types/combineHSA.types';

const COMBINE_HSA_PERMISSIONS = [{ permission: Permissions.COMBINE_HSA }];

interface Params {
  accountNumber: string;
  employeeId: string;
  isHSA: boolean;
  firstName?: string;
  lastName?: string;
}

export const useCombineHSATrigger = ({
  accountNumber,
  employeeId,
  isHSA,
  firstName,
  lastName,
}: Params) => {
  const { observingOrganization: { type } } = useCurrentOrganization();
  const isAllowedOrgLevel = type === OrganizationTypes.partner || type === OrganizationTypes.system;

  const hasCombineHSAPermission = useHasAccess(COMBINE_HSA_PERMISSIONS);
  const isCombineHSAPreCheckEnabled = isHSA && hasCombineHSAPermission && isAllowedOrgLevel;

  const { ssn, isLoading: isSsnLoading } = useGetEmployeeSSNQuery(isCombineHSAPreCheckEnabled ? employeeId : undefined);
  const { data: fromAccounts, isLoading: isFromAccountsLoading } = useAutoSearchHSACombineAccounts({
    accountNumber,
    employeeId,
    ssn,
    firstName,
    lastName,
    sectionType: CombineHSASectionType.FROM,
  });
  const { data: toAccounts, isLoading: isToAccountsLoading } = useAutoSearchHSACombineAccounts({
    accountNumber,
    employeeId,
    ssn,
    firstName,
    lastName,
    sectionType: CombineHSASectionType.TO,
  });

  const hasEligibleAccountForFrom = (fromAccounts ?? []).length > 0;
  const hasEligibleAccountForTo = (toAccounts ?? []).some((account) => account.accountNumber !== accountNumber);
  const isCombineHSATriggerVisible =
    isCombineHSAPreCheckEnabled &&
    (hasEligibleAccountForFrom || hasEligibleAccountForTo);

  const isCombineHSATriggerLoading =
    isCombineHSAPreCheckEnabled && (isSsnLoading || isFromAccountsLoading || isToAccountsLoading);

  return {
    isCombineHSATriggerVisible,
    isCombineHSATriggerLoading,
  };
};
